import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Edit,
  Send,
  Coins,
  Loader,
  Calendar,
  Building2,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  Printer,
  FileText,
  Globe,
  CreditCard,
  User,
  RefreshCw,
  CheckCircle,
  FileCode2,
  Landmark
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchInvoiceById,
  fetchUsage,
  markInvoiceSent,
  markInvoicePaid,
  markInvoiceConfirmed,
  setCurrentInvoice
} from '../../store/invoiceSlice';
import { downloadAndSavePdf, generateInvoicePdf, downloadAndSaveXml, getInvoiceStatusLabel } from '../../api/invoices';
import { KsefChip } from './ksef/KsefChip';
import KsefSubmitModal from './ksef/KsefSubmitModal';
import { KsefInvoiceState, resolveKsefEnv } from '../../api/ksef';
import { formatMoney } from '../../utils/formatters';
import { parseBlobError } from '../../utils/errors';
import { useToast } from '../ui/Toast';
import { InvoiceStatus, InvoiceType, InvoiceLanguage } from '../../types';
import { createTranslator, getInvoiceTypeTitle, TranslationKey } from '../../utils/invoiceTranslations';
import { useSubscription } from '../../hooks/useSubscription';

// Status badge — token-driven chip classes from globals.css (light & dark theme aware)
const getStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case 'paid':             return 'chip chip-paid';
    case 'sent':             return 'chip chip-sent';
    case 'ready_to_process': return 'chip chip-ready';
    case 'confirmed':        return 'chip chip-confirmed';
    case 'overdue':          return 'chip chip-overdue';
    case 'cancelled':        return 'chip chip-cancelled';
    default:                 return 'chip chip-draft';
  }
};

// Invoice type badge colors with light/dark theme support
const getInvoiceTypeBadge = (type: InvoiceType) => {
  switch (type) {
    case 'vat':
      return { label: 'VAT', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30' };
    case 'reverse_charge':
      return { label: 'Reverse Charge', color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30' };
    case 'proforma':
      return { label: 'PROFORMA', color: 'bg-coral-100 text-coral-700 border-coral-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:border-coral-500/30' };
    case 'credit_note':
      return { label: 'Credit Note', color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/30' };
    default:
      return null;
  }
};

const getLanguageLabel = (lang: InvoiceLanguage): string => {
  const labels: Record<InvoiceLanguage, string> = {
    en: 'English',
    pl: 'Polski',
    ro: 'Română'
  };
  return labels[lang] || lang;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  PLN: 'zł',
  RON: 'lei',
  CAD: '$',
  AUD: '$'
};

const InvoicePreview: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { currentInvoice, usage, loading, error } = useAppSelector((state) => state.invoices);

  const [downloading, setDownloading] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [showKsefModal, setShowKsefModal] = useState(false);
  const [ksefState, setKsefState] = useState<KsefInvoiceState | null>(null);
  const { isKsefEligible } = useSubscription();
  const [actionLoading, setActionLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Create translator based on invoice language settings
  const translator = useMemo(() => {
    if (!currentInvoice) return createTranslator('en');
    return createTranslator(
      currentInvoice.language || 'en',
      currentInvoice.secondary_language || undefined
    );
  }, [currentInvoice?.language, currentInvoice?.secondary_language]);

  const t = (key: TranslationKey) => translator.t(key);

  // Delegates to the app-wide toast; this page used to render its own.
  const showNotification = (type: 'success' | 'error', message: string) => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchInvoiceById(parseInt(id)));
      dispatch(fetchUsage());
    }

    return () => {
      dispatch(setCurrentInvoice(null));
    };
  }, [dispatch, id]);

  // `usage === null` means the usage fetch has not landed (or failed) — it does
  // NOT mean the limit is reached. Treating it as "blocked" showed a full-page
  // "Creation Limit Reached" screen and a false "You have reached your PDF
  // download limit" banner to users who were nowhere near their limit. The
  // backend enforces the real limit on every request, so optimistically
  // allowing here is safe.
  const canEdit = usage ? usage.invoices_edited < usage.invoices_edited_limit : true;
  const canDownload = usage ? usage.invoice_downloads < usage.invoice_downloads_limit : true;

  const handleDownload = async () => {
    if (!currentInvoice || !canDownload) return;

    try {
      setDownloading(true);
      // Always regenerate PDF to ensure language settings are current
      await generateInvoicePdf(currentInvoice.id);
      await downloadAndSavePdf(currentInvoice.id, currentInvoice.invoice_number);
      dispatch(fetchUsage());
      showNotification('success', 'PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      showNotification('error', 'Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadXml = async () => {
    if (!currentInvoice) return;
    try {
      setDownloadingXml(true);
      await downloadAndSaveXml(currentInvoice.id, currentInvoice.invoice_number);
      showNotification('success', 'KSeF XML downloaded successfully');
    } catch (err: any) {
      showNotification('error', await parseBlobError(err, 'Failed to generate KSeF XML.'));
    } finally {
      setDownloadingXml(false);
    }
  };

  const handleRegeneratePdf = async () => {
    if (!currentInvoice) return;

    try {
      setRegenerating(true);
      await generateInvoicePdf(currentInvoice.id);
      dispatch(fetchInvoiceById(currentInvoice.id));
      showNotification('success', 'PDF regenerated successfully');
    } catch (err) {
      console.error('Failed to regenerate PDF:', err);
      showNotification('error', 'Failed to regenerate PDF. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleMarkConfirmed = async () => {
    if (!currentInvoice) return;
    try {
      setActionLoading(true);
      await dispatch(markInvoiceConfirmed(currentInvoice.id)).unwrap();
      showNotification('success', 'Invoice confirmed');
    } catch (err) {
      console.error('Failed to confirm invoice:', err);
      showNotification('error', 'Failed to update invoice status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkSent = async () => {
    if (!currentInvoice) return;
    try {
      setActionLoading(true);
      await dispatch(markInvoiceSent(currentInvoice.id)).unwrap();
      showNotification('success', 'Invoice marked as sent');
    } catch (err) {
      console.error('Failed to mark as sent:', err);
      showNotification('error', 'Failed to update invoice status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!currentInvoice) return;
    try {
      setActionLoading(true);
      await dispatch(markInvoicePaid(currentInvoice.id)).unwrap();
      showNotification('success', 'Invoice marked as paid');
    } catch (err) {
      console.error('Failed to mark as paid:', err);
      showNotification('error', 'Failed to update invoice status');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 text-coral-600 dark:text-coral-400 animate-spin" />
      </div>
    );
  }

  if (!currentInvoice) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-orange-500 dark:text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">Invoice Not Found</h2>
          <button
            onClick={() => navigate('/created-invoices')}
            className="px-4 py-2 bg-coral-600 hover:bg-coral-700 text-white rounded-lg transition-colors"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const invoice = currentInvoice;
  const client = invoice.client;
  const seller = invoice.seller;
  const currencySymbol = CURRENCY_SYMBOLS[invoice.currency] || invoice.currency;
  const invoiceTypeBadge = getInvoiceTypeBadge(invoice.invoice_type);

  // Calculate amounts
  const paidAmount = invoice.paid_amount || 0;
  const amountDue = invoice.amount_due !== undefined ? invoice.amount_due : invoice.total - paidAmount;

  // Format address helper
  const formatAddress = (entity: { street?: string; street_number?: string; city?: string; state?: string; postal_code?: string; country?: string }) => {
    const parts: string[] = [];
    if (entity.street) {
      parts.push(entity.street_number ? `${entity.street} ${entity.street_number}` : entity.street);
    }
    if (entity.city || entity.state || entity.postal_code) {
      parts.push([entity.city, entity.state, entity.postal_code].filter(Boolean).join(', '));
    }
    if (entity.country) {
      parts.push(entity.country);
    }
    return parts;
  };

  return (
    <div className="container mx-auto px-4 py-8">

      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/created-invoices')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-coral-500/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-coral-400" />
          </button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {invoice.invoice_type === 'proforma' ? 'Proforma ' : ''}
                {invoice.invoice_type === 'credit_note' ? 'Credit Note ' : 'Invoice '}
                {invoice.invoice_number}
              </h1>
              {invoiceTypeBadge && (
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${invoiceTypeBadge.color}`}>
                  {invoiceTypeBadge.label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {invoice.direction === 'incoming' && (
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Incoming · money out
                </div>
              )}
              <div className={getStatusColor(invoice.status)}>
                {getInvoiceStatusLabel(invoice.status, invoice.direction)}
              </div>
              <span className="text-sm text-foreground-muted flex items-center gap-1.5">
                <Globe className="w-4 h-4" />
                {getLanguageLabel(invoice.language)}
                {invoice.secondary_language && (
                  <span className="text-primary font-medium">+ {getLanguageLabel(invoice.secondary_language)}</span>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(invoice.status === 'ready_to_process' || invoice.status === 'confirmed') && canEdit && (
            <button
              onClick={() => navigate(`/created-invoices/${invoice.id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:hover:bg-coral-500/30 rounded-lg transition-colors font-medium"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading || !canDownload}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              canDownload
                ? 'bg-coral-600 text-white hover:bg-coral-700 dark:bg-coral-600 dark:hover:bg-coral-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-coral-500/10 dark:text-foreground-muted'
            }`}
            title={canDownload ? 'Download PDF' : 'Download limit reached'}
          >
            {downloading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download PDF
          </button>
          {currentInvoice.invoice_type !== 'proforma' && (
            <button
              onClick={handleDownloadXml}
              disabled={downloadingXml}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:hover:bg-coral-500/30 rounded-lg transition-colors font-medium disabled:opacity-50"
              title="Download KSeF FA(3) XML"
            >
              {downloadingXml ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <FileCode2 className="w-4 h-4" />
              )}
              Download XML
            </button>
          )}
          {currentInvoice.invoice_type !== 'proforma' &&
            isKsefEligible &&
            ['none', 'rejected', undefined].includes(
              (ksefState?.status || (currentInvoice as any).ksef?.status) as any
            ) && (
              <button
                onClick={() => setShowKsefModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-coral-600 text-white hover:bg-coral-700 rounded-lg transition-colors font-medium"
                title="Submit to KSeF"
              >
                <Landmark className="w-4 h-4" />
                Submit to KSeF
              </button>
            )}
          {(ksefState?.status || (currentInvoice as any).ksef?.status) &&
            (ksefState?.status || (currentInvoice as any).ksef?.status) !== 'none' && (
              <KsefChip status={(ksefState?.status || (currentInvoice as any).ksef?.status)} />
            )}
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:hover:bg-coral-500/30 rounded-lg transition-colors font-medium"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={handleRegeneratePdf}
            disabled={regenerating}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:hover:bg-coral-500/30 rounded-lg transition-colors font-medium disabled:opacity-50"
            title="Regenerate PDF with current settings"
          >
            {regenerating ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Regenerate
          </button>
          {invoice.status === 'ready_to_process' && (
            <button
              onClick={handleMarkConfirmed}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-coral-100 text-coral-700 hover:bg-coral-200 dark:bg-coral-500/20 dark:text-foreground-muted dark:hover:bg-coral-500/30 rounded-lg transition-colors font-medium"
            >
              {actionLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Confirm
            </button>
          )}
          {invoice.status === 'confirmed' && (
            <button
              onClick={handleMarkSent}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-yellow-500/20 dark:text-yellow-300 dark:hover:bg-yellow-500/30 rounded-lg transition-colors font-medium"
            >
              {actionLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Mark Sent
            </button>
          )}
          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
            <button
              onClick={handleMarkPaid}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-500/20 dark:text-green-300 dark:hover:bg-green-500/30 rounded-lg transition-colors font-medium"
            >
              {actionLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Coins className="w-4 h-4" />
              )}
              Mark Paid
            </button>
          )}
        </div>
      </div>

      {/* Invoice preview - Paper style */}
      <div className="bg-white dark:bg-background-secondary rounded-xl shadow-lg border border-gray-200 dark:border-coral-500/20 overflow-hidden print:border-none print:shadow-none print:rounded-none">
        {/* Invoice header */}
        <div className="p-8 border-b border-gray-200 dark:border-coral-500/10 print:border-gray-300">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div>
              <h2 className="text-3xl font-bold text-coral-700 dark:text-coral-400 mb-2 print:text-coral-800">
                {getInvoiceTypeTitle(invoice.invoice_type, invoice.language || 'en', invoice.secondary_language || undefined)}
              </h2>
              <p className="text-gray-500 dark:text-foreground-muted text-lg">#{invoice.invoice_number}</p>
              {invoice.place_of_issue && (
                <p className="text-sm text-gray-500 dark:text-foreground-muted mt-2">
                  {t('place_of_issue')}: {invoice.place_of_issue}
                </p>
              )}
            </div>
            <div className="text-right space-y-2">
              <div className="flex items-center justify-end gap-2 text-gray-600 dark:text-foreground-muted">
                <Calendar className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                <span className="font-medium">{t('invoice_date')}:</span>
                <span>{invoice.invoice_date ? formatDate(invoice.invoice_date) : '-'}</span>
              </div>
              {invoice.sale_date && (
                <div className="flex items-center justify-end gap-2 text-gray-600 dark:text-foreground-muted">
                  <Calendar className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                  <span className="font-medium">{t('sale_date')}:</span>
                  <span>{formatDate(invoice.sale_date)}</span>
                </div>
              )}
              {invoice.due_date && (
                <div className="flex items-center justify-end gap-2 text-gray-600 dark:text-foreground-muted">
                  <Calendar className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                  <span className="font-medium">{t('due_date')}:</span>
                  <span>{formatDate(invoice.due_date)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Proforma Notice */}
        {invoice.invoice_type === 'proforma' && (
          <div className="px-8 py-4 bg-coral-50 dark:bg-coral-500/5 border-b border-coral-100 dark:border-coral-500/10 print:bg-coral-50">
            <p className="text-sm text-coral-700 dark:text-coral-400 italic">
              {t('proforma_notice')}
            </p>
          </div>
        )}

        {/* Reverse Charge Legal Notice — only shown when explicitly enabled */}
        {invoice.invoice_type === 'reverse_charge' && invoice.show_reverse_charge_notice && (
          <div className="px-8 py-4 bg-red-50 dark:bg-red-500/5 border-b border-red-100 dark:border-red-500/10 print:bg-red-50">
            <p className="text-sm text-red-700 dark:text-red-400 italic font-medium">
              {t('reverse_charge_notice')}
            </p>
          </div>
        )}

        {/* Credit Note Reference */}
        {invoice.invoice_type === 'credit_note' && invoice.original_invoice_id && (
          <div className="px-8 py-4 bg-red-50 dark:bg-red-500/5 border-b border-red-100 dark:border-red-500/10 print:bg-red-50">
            <p className="text-sm text-red-700 dark:text-red-400">
              {t('credit_note_reference')}: #{invoice.original_invoice_id}
            </p>
          </div>
        )}

        {/* From / To sections */}
        <div className="p-8 border-b border-gray-200 dark:border-coral-500/10 print:border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Seller (From) */}
            {seller && (
              <div>
                <h3 className="text-sm font-semibold text-coral-600 dark:text-coral-400 mb-4 uppercase tracking-wider flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {t('seller')}
                </h3>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-gray-900 dark:text-foreground">
                    {seller.name}
                  </p>
                  {seller.tax_id && (
                    <p className="text-gray-600 dark:text-foreground-muted">{t('tax_id')}: {seller.tax_id}</p>
                  )}
                  {(seller.street || seller.city) && (
                    <div className="flex items-start gap-2 text-gray-600 dark:text-foreground-muted">
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-coral-500 dark:text-coral-400" />
                      <div>
                        {formatAddress(seller).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  {seller.email && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-foreground-muted">
                      <Mail className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                      <span>{seller.email}</span>
                    </div>
                  )}
                  {seller.phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-foreground-muted">
                      <Phone className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                      <span>{seller.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Client (Bill To) */}
            {client && (
              <div>
                <h3 className="text-sm font-semibold text-coral-600 dark:text-coral-400 mb-4 uppercase tracking-wider flex items-center gap-2">
                  {client.is_business ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  {t('bill_to')}
                </h3>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-gray-900 dark:text-foreground">
                    {client.name}
                  </p>
                  {client.is_business && client.tax_id && (
                    <p className="text-gray-600 dark:text-foreground-muted">{t('tax_id')}: {client.tax_id}</p>
                  )}
                  {(client.street || client.city) && (
                    <div className="flex items-start gap-2 text-gray-600 dark:text-foreground-muted">
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-coral-500 dark:text-coral-400" />
                      <div>
                        {formatAddress(client).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  {client.email && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-foreground-muted">
                      <Mail className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                      <span>{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-foreground-muted">
                      <Phone className="w-4 h-4 text-coral-500 dark:text-coral-400" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bank Details (if seller has them) */}
        {seller && (seller.bank_name || seller.bank_account_number || seller.bank_iban) && (
          <div className="px-8 py-5 bg-gray-50 dark:bg-coral-500/5 border-b border-gray-200 dark:border-coral-500/10 print:bg-gray-50">
            <h3 className="text-sm font-semibold text-coral-600 dark:text-coral-400 mb-3 uppercase tracking-wider flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              {t('bank_details')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              {seller.bank_name && (
                <div>
                  <span className="text-gray-500 dark:text-foreground-muted block mb-0.5">{t('bank_name')}</span>
                  <span className="text-gray-800 dark:text-foreground-secondary font-medium">{seller.bank_name}</span>
                </div>
              )}
              {seller.bank_iban && (
                <div>
                  <span className="text-gray-500 dark:text-foreground-muted block mb-0.5">{t('iban')}</span>
                  <span className="text-gray-800 dark:text-foreground-secondary font-mono font-medium">{seller.bank_iban}</span>
                </div>
              )}
              {seller.bank_swift && (
                <div>
                  <span className="text-gray-500 dark:text-foreground-muted block mb-0.5">{t('swift')}</span>
                  <span className="text-gray-800 dark:text-foreground-secondary font-mono font-medium">{seller.bank_swift}</span>
                </div>
              )}
              {seller.bank_account_number && !seller.bank_iban && (
                <div>
                  <span className="text-gray-500 dark:text-foreground-muted block mb-0.5">{t('account_number')}</span>
                  <span className="text-gray-800 dark:text-foreground-secondary font-mono font-medium">{seller.bank_account_number}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Items table */}
        <div className="p-8 border-b border-gray-200 dark:border-coral-500/10 print:border-gray-300">
          <h3 className="text-sm font-semibold text-coral-600 dark:text-coral-400 mb-4 uppercase tracking-wider">
            {t('items')}
          </h3>
          <div className="overflow-x-auto -mx-8 px-8">
            <table className="w-full min-w-[600px] border border-coral-300 dark:border-coral-500/40 print:border-coral-300">
              <thead>
                <tr className="bg-coral-50 dark:bg-coral-500/10 border-b border-coral-300 dark:border-coral-500/40 print:bg-coral-50 print:border-coral-300">
                  <th className="text-center py-3 px-3 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-8">#</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800">
                    {t('description')}
                  </th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-20">
                    {t('unit')}
                  </th>
                  <th className="text-right py-3 px-3 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-20">
                    {t('quantity')}
                  </th>
                  <th className="text-right py-3 px-3 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-28">
                    {t('unit_price')}
                  </th>
                  <th className="text-right py-3 px-3 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-28">
                    {t('total')}
                  </th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-coral-700 dark:text-coral-400 print:text-coral-800 w-24">
                    {t('tax_rate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-coral-500/5">
                    <td className="py-3 px-3 text-center text-gray-500 dark:text-foreground-muted text-sm">{index + 1}</td>
                    <td className="py-3 px-4 text-gray-800 dark:text-foreground">
                      <span className="font-medium">{item.description}</span>
                    </td>
                    <td className="py-3 px-2 text-center text-gray-600 dark:text-foreground-muted text-sm">
                      {item.unit || '—'}
                    </td>
                    <td className="py-3 px-3 text-right text-gray-600 dark:text-foreground-muted tabular-nums">
                      {item.quantity}
                    </td>
                    <td className="py-3 px-3 text-right text-gray-600 dark:text-foreground-muted tabular-nums">
                      {currencySymbol}{item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-right font-semibold text-gray-800 dark:text-foreground tabular-nums">
                      {currencySymbol}{item.line_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-2 text-center text-gray-600 dark:text-foreground-muted text-sm">
                      {invoice.invoice_type === 'reverse_charge'
                        ? <span className="text-xs leading-tight">reverse charge<br />o.o.</span>
                        : item.tax_rate != null
                          ? `${item.tax_rate}%`
                          : invoice.tax_rate
                            ? `${invoice.tax_rate}%`
                            : '0%'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="p-8 border-b border-gray-200 dark:border-coral-500/10 print:border-gray-300 bg-gray-50/50 dark:bg-transparent">
          <div className="flex justify-end">
            <div className="w-full max-w-sm space-y-3">
              <div className="flex justify-between text-gray-600 dark:text-foreground-muted">
                <span>{t('subtotal')}</span>
                <span className="tabular-nums font-medium">
                  {currencySymbol}{invoice.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              {invoice.tax_rate && invoice.tax_rate > 0 && (
                <div className="flex justify-between text-gray-600 dark:text-foreground-muted">
                  <span>{t('tax')} ({invoice.tax_rate}%)</span>
                  <span className="tabular-nums font-medium">
                    {currencySymbol}{invoice.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {invoice.discount && invoice.discount > 0 && (
                <div className="flex justify-between text-gray-600 dark:text-foreground-muted">
                  <span>{t('discount')}</span>
                  <span className="text-red-600 dark:text-red-400 tabular-nums font-medium">
                    -{currencySymbol}{invoice.discount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold pt-4 border-t-2 border-coral-200 dark:border-coral-500/30 print:border-coral-300 text-coral-700 dark:text-foreground-muted">
                <span>{t('total_due')}</span>
                <span className="tabular-nums">
                  {currencySymbol}{invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}{' '}
                  <span className="text-base font-normal text-gray-500 dark:text-coral-400">{invoice.currency}</span>
                </span>
              </div>

              {/* Payment Status */}
              {paidAmount > 0 && (
                <>
                  <div className="flex justify-between text-green-700 dark:text-green-400 pt-2">
                    <span>{t('amount_paid')}</span>
                    <span className="tabular-nums font-medium">
                      {currencySymbol}{paidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {amountDue > 0 && (
                    <div className="flex justify-between text-lg font-semibold text-orange-600 dark:text-orange-400">
                      <span>{t('balance_due')}</span>
                      <span className="tabular-nums">
                        {currencySymbol}{amountDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Due Date */}
              {invoice.due_date && (
                <div className="flex justify-between text-sm text-gray-500 dark:text-foreground-muted pt-2 border-t border-gray-100 dark:border-coral-500/10">
                  <span className="font-medium">{t('due_date')}</span>
                  <span>{formatDate(invoice.due_date)}</span>
                </div>
              )}

              {/* Payment Terms */}
              {invoice.terms && (
                <div className="flex justify-between text-sm text-gray-500 dark:text-foreground-muted">
                  <span className="font-medium">{t('payment_terms')}</span>
                  <span className="text-right max-w-[60%]">{invoice.terms}</span>
                </div>
              )}

              {/* Payment Method */}
              {invoice.payment_method && invoice.status === 'paid' && (
                <div className="flex justify-between text-sm text-gray-500 dark:text-foreground-muted pt-2">
                  <span>{t('payment_method')}</span>
                  <span className="capitalize font-medium">{invoice.payment_method.replace('_', ' ')}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Optional notes (replaces old Terms & Conditions position) */}
        {invoice.notes && (
          <div className="p-8 border-b border-gray-200 dark:border-coral-500/10 print:border-gray-300">
            <p className="text-gray-600 dark:text-foreground-muted whitespace-pre-wrap text-sm leading-relaxed">
              {invoice.notes}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-4 border-t border-coral-100 dark:border-coral-500/20 text-center print:border-coral-200">
          <p className="text-xs text-foreground-muted dark:text-foreground-muted print:text-coral-400">
            Generated with ViewPoint &nbsp;|&nbsp; {t('currency')}: {invoice.currency}
          </p>
        </div>
      </div>

      {/* Download limit warning */}
      {!canDownload && (
        <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg print:hidden">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 dark:text-orange-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-orange-700 dark:text-orange-300 font-medium">
                You have reached your PDF download limit for this period.
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 bg-coral-600 hover:bg-coral-700 text-white rounded-lg transition-colors font-medium"
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      )}

      {showKsefModal && currentInvoice && (
        <KsefSubmitModal
          invoiceId={currentInvoice.id}
          invoiceNumber={currentInvoice.invoice_number}
          buyerName={currentInvoice.client?.name}
          total={formatMoney(currentInvoice.total, currentInvoice.currency)}
          environment={resolveKsefEnv(currentInvoice)}
          onClose={() => setShowKsefModal(false)}
          onDone={(ksef) => setKsefState(ksef)}
        />
      )}
    </div>
  );
};

export default InvoicePreview;
